import React, { useEffect, useState } from 'react';
import { TrendingUp, ArrowUpRight, IndianRupee, Loader2, Wallet, Gift, AlertOctagon, Banknote, CalendarClock } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import DesktopHeader from '../components/DesktopHeader';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const CleanerEarnings = () => {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [settlement, setSettlement] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/cleaner/earnings`, authCfg()),
      axios.get(`${API}/cleaner/my-wallet`, authCfg()).catch(() => ({ data: null })),
      axios.get(`${API}/cleaner/my-settlement`, authCfg()).catch(() => ({ data: null })),
    ]).then(([e, w, s]) => {
      setData(e.data); setWallet(w.data); setSettlement(s.data);
    }).catch((e) => { if (e?.response?.status === 401) nav('/cleaner/login'); })
    .finally(() => setLoading(false));
  }, [nav]);

  if (loading) return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
    </div>
  );

  const d = data || { total_earned: 0, this_month: 0, total_jobs: 0, this_month_jobs: 0, avg_per_job: 0, in_progress: 0, payouts: [] };

  return (
    <div className="min-h-screen bg-neutral-50 pb-24 md:pb-8">
      <DesktopHeader />

      <div className="max-w-[1200px] mx-auto px-5 md:px-6 pt-4 md:pt-8">
        <h1 className="text-xl md:text-2xl font-extrabold">Earnings & Wallet</h1>
        <p className="text-sm text-neutral-500 mt-0.5">Track your payouts from finished jobs, plus bonuses, penalties and advances.</p>

        <div className="grid md:grid-cols-3 gap-4 mt-4">
          {/* Main balance card - simplified */}
          <div data-testid="earnings-this-month-card" className="md:col-span-1 bg-white border border-neutral-200 rounded-2xl p-5">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
                <Wallet className="w-5 h-5 text-[#2E7D32]" />
              </div>
              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">This month</p>
            </div>
            <div className="mt-3 flex items-baseline gap-1 text-neutral-900">
              <IndianRupee className="w-5 h-5" strokeWidth={2.5} />
              <span className="text-3xl font-extrabold" data-testid="earnings-this-month-value">{d.this_month.toLocaleString('en-IN')}</span>
            </div>
            <p className="text-xs text-neutral-500 mt-1">{d.this_month_jobs} finished job{d.this_month_jobs === 1 ? '' : 's'} · Lifetime ₹{d.total_earned.toLocaleString('en-IN')}</p>
          </div>

          <div className="md:col-span-2 grid grid-cols-3 gap-3">
            <SmallStat label="Total jobs" value={d.total_jobs} testid="earnings-total-jobs" />
            <SmallStat label="Avg / job" value={`₹${(d.avg_per_job || 0).toLocaleString('en-IN')}`} testid="earnings-avg-job" />
            <SmallStat label="In progress" value={d.in_progress} testid="earnings-in-progress" />
          </div>
        </div>

        {/* Wallet breakdown (bonus/incentives/penalties/advances/pending) */}
        {wallet && (
          <div className="mt-6" data-testid="cleaner-wallet-breakdown">
            <h2 className="text-base md:text-lg font-bold flex items-center gap-1.5"><Wallet className="w-4 h-4 text-[#2E7D32]" /> Wallet</h2>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatChip label="Bonus"     value={wallet.bonus}                icon={Gift}         color="#7B1FA2" bg="#F3E5F5" testid="cleaner-bonus" />
              <StatChip label="Incentives" value={wallet.incentives}          icon={TrendingUp}   color="#059669" bg="#D1FAE5" testid="cleaner-incentives" />
              <StatChip label="Penalty"    value={wallet.penalty}             icon={AlertOctagon} color="#DC2626" bg="#FEE2E2" testid="cleaner-penalty" negative />
              <StatChip label="Cancellation" value={wallet.cancellation_charges} icon={AlertOctagon} color="#DC2626" bg="#FEE2E2" testid="cleaner-cancellation" negative />
              <StatChip label="Advance received" value={wallet.advance_received} icon={Banknote} color="#EA580C" bg="#FFEDD5" testid="cleaner-advance" negative />
              <StatChip label="Pending Payment" value={wallet.pending_payment} icon={IndianRupee}  color="#2E7D32" bg="#E8F5E9" testid="cleaner-pending-payment" big />
            </div>
          </div>
        )}

        {/* Weekly settlement */}
        {settlement && (
          <div className="mt-6 bg-white border border-neutral-200 rounded-2xl p-5" data-testid="cleaner-weekly-settlement">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
                <CalendarClock className="w-4 h-4 text-[#2E7D32]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">This week's settlement</p>
                <p className="font-extrabold">{settlement.week_start} → {settlement.week_end}</p>
              </div>
            </div>
            <ul className="text-sm divide-y divide-neutral-100">
              <SettleRow label="Earned from jobs"  value={settlement.earned}       positive />
              <SettleRow label="Bonus"             value={settlement.bonus}        positive />
              <SettleRow label="Incentives"        value={settlement.incentives}   positive />
              <SettleRow label="Penalty"           value={-settlement.penalty} />
              <SettleRow label="Advance received" value={-settlement.advance} />
              <SettleRow label="Cancellation charges" value={-settlement.cancellation_charges} />
              <li className="flex items-center justify-between py-2 mt-1 font-extrabold" data-testid="cleaner-net-payout">
                <span>Net payout</span>
                <span className={settlement.net_payout >= 0 ? 'text-[#2E7D32]' : 'text-red-600'}>₹{settlement.net_payout.toLocaleString('en-IN')}</span>
              </li>
            </ul>
            <p className="mt-2 text-[10px] text-neutral-500">Franchise Owner approves this every Monday; approved payouts appear below.</p>
          </div>
        )}

        {/* Bank Transfer History */}
        {wallet && wallet.bank_transfers && wallet.bank_transfers.length > 0 && (
          <div className="mt-6" data-testid="cleaner-bank-transfers">
            <h2 className="text-base md:text-lg font-bold flex items-center gap-1.5"><Banknote className="w-4 h-4 text-[#2E7D32]" /> Bank transfer history</h2>
            <div className="mt-3 space-y-2">
              {wallet.bank_transfers.map((t) => (
                <div key={t.id} className="bg-white border border-neutral-200 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                    <Banknote className="w-4 h-4 text-[#2E7D32]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">Week {t.week_start} → {t.week_end}</p>
                    <p className="text-xs text-neutral-500">{new Date(t.created_at).toLocaleDateString('en-IN')} · {t.method || 'bank_transfer'} {t.reference ? `· ref ${t.reference}` : ''}</p>
                  </div>
                  <p className="font-bold text-sm text-[#2E7D32]">+₹{t.amount}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6">
          <h2 className="text-base md:text-lg font-bold">Recent payouts (from finished jobs)</h2>
          {d.payouts.length === 0 ? (
            <div className="mt-3 bg-white border border-dashed border-neutral-200 rounded-xl p-8 text-center">
              <ArrowUpRight className="w-8 h-8 text-neutral-300 mx-auto" />
              <p className="mt-2 text-sm font-semibold text-neutral-700">No payouts yet</p>
              <p className="text-xs text-neutral-500 mt-1">Finish your first accepted job to see earnings here.</p>
            </div>
          ) : (
            <div className="mt-3 space-y-2" data-testid="earnings-payouts-list">
              {d.payouts.map((t) => (
                <div key={t.id} className="bg-white border border-neutral-200 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                    <ArrowUpRight className="w-4 h-4 text-[#2E7D32]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{t.title}</p>
                    <p className="text-xs text-neutral-500">{t.id} · {t.date ? new Date(t.date).toLocaleDateString('en-IN') : ''}</p>
                  </div>
                  <p className="font-bold text-sm text-[#2E7D32]">+₹{t.amount}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

const SmallStat = ({ label, value, testid }) => (
  <div data-testid={testid} className="bg-white border border-neutral-200 rounded-xl p-4">
    <p className="text-xs text-neutral-500">{label}</p>
    <p className="font-bold text-lg mt-1">{value}</p>
  </div>
);

const StatChip = ({ label, value, icon: Icon, color, bg, testid, negative = false, big = false }) => (
  <div data-testid={testid} className={`bg-white border border-neutral-200 rounded-xl p-3 ${big ? 'md:col-span-2' : ''}`}>
    <div className="flex items-center gap-1.5">
      <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: bg }}>
        <Icon className="w-3.5 h-3.5" style={{ color }} />
      </div>
      <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{label}</p>
    </div>
    <p className={`mt-1.5 font-extrabold ${big ? 'text-xl' : 'text-base'}`} style={{ color: negative ? '#DC2626' : color }}>
      {negative && (value || 0) > 0 ? '−' : ''}₹{(value || 0).toLocaleString('en-IN')}
    </p>
  </div>
);

const SettleRow = ({ label, value, positive = false }) => (
  <li className="flex items-center justify-between py-1.5 text-sm">
    <span className="text-neutral-700">{label}</span>
    <span className={`font-bold ${value < 0 ? 'text-red-600' : positive ? 'text-[#2E7D32]' : ''}`}>
      {value < 0 ? '−' : ''}₹{Math.abs(value || 0).toLocaleString('en-IN')}
    </span>
  </li>
);

export default CleanerEarnings;
